import { useEffect, useState } from 'react'
import {
  Form as AntdForm,
  Modal,
  DatePicker as AntdDatePicker,
  Divider,
  Upload,
  UploadFile
} from 'antd'
import { PlusOutlined, UploadOutlined } from '@ant-design/icons'
import { FormItem, Input, Button, Heading, TextArea, Text } from '@lmc/ui-kit'
import styled from 'styled-components'
import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { referralConstants } from 'locale/referrals'
import { ReferralResponse, ReferralCreate } from '@lmc/validations'
import { MAX_ATTACHMENTS } from '../constants'
import { useReferralCreate } from '../hooks/useReferralCreate'
import { useReferralUpdate } from '../hooks/useReferralUpdate'

type Referral = ReferralResponse
type CreateReferralInput = ReferralCreate

dayjs.extend(utc)

export type ReferralModalProps = {
  isVisible: boolean
  onClose: () => void
  onSuccess: () => void
  referral?: Referral
  residentId: string
}

const ReferralModal = ({
  isVisible,
  onClose,
  onSuccess,
  referral,
  residentId
}: ReferralModalProps) => {
  const { t } = useTranslation(NAMESPACE.REFERRALS)
  const [form] = AntdForm.useForm()
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const isEditMode = !!referral
  const existingAttachmentsCount = referral?.attachments?.length || 0
  const remainingSlots = MAX_ATTACHMENTS - existingAttachmentsCount

  const { createReferral, isCreating } = useReferralCreate(() => {
    onSuccess()
    onClose()
  })

  const { updateReferral, isUpdating } = useReferralUpdate(residentId, () => {
    onSuccess()
    onClose()
  })

  useEffect(() => {
    if (isVisible) {
      if (referral) {
        // Parse date for display
        const dateValue = referral.referralDate
          ? dayjs(referral.referralDate)
          : undefined

        form.setFieldsValue({
          reasonForReferral: referral.reasonForReferral || '',
          referredTo: referral.referredTo || '',
          referralDate: dateValue
        })
        // File uploads can't be prefilled, so fileList stays empty
        setFileList([])
      } else {
        form.resetFields()
        setFileList([])
      }
    }
  }, [isVisible, referral, form])

  const handleFileChange = (info: { fileList: UploadFile[] }) => {
    // Limit based on remaining slots (MAX_ATTACHMENTS - existing attachments)
    const maxAllowed = isEditMode ? remainingSlots : MAX_ATTACHMENTS
    const limitedFileList = info.fileList.slice(-maxAllowed)
    setFileList(limitedFileList)
  }

  const handleSubmit = async (values: unknown) => {
    const formValues = values as {
      reasonForReferral: string
      referredTo: string
      referralDate?: Dayjs
    }

    const submitValues: CreateReferralInput = {
      resident: residentId as any,
      reasonForReferral: formValues.reasonForReferral,
      referredTo: formValues.referredTo,
      referralDate: formValues.referralDate
        ? formValues.referralDate.format('YYYY-MM-DD')
        : ''
    }

    // Remove undefined values
    const cleanedSubmitValues = Object.fromEntries(
      Object.entries(submitValues).filter(([_, value]) => value !== undefined)
    ) as CreateReferralInput

    if (isEditMode && referral) {
      await updateReferral({ _id: referral._id, ...cleanedSubmitValues }, fileList)
    } else {
      await createReferral(cleanedSubmitValues, fileList)
    }
  }

  if (!isVisible) return null

  return (
    <Modal
      open={isVisible}
      onCancel={onClose}
      destroyOnClose
      footer={
        <ModalFooter>
          <Button onClick={onClose}>{t(referralConstants.CANCEL)}</Button>
          <Button
            onClick={form.submit}
            variant="primary"
            isLoading={isEditMode ? isUpdating : isCreating}
          >
            {isEditMode ? (
              t(referralConstants.SAVE)
            ) : (
              <>
                <PlusOutlined />
                {t(referralConstants.ADD)}
              </>
            )}
          </Button>
        </ModalFooter>
      }
      width={600}
    >
      <Heading level={3}>
        {isEditMode
          ? t(referralConstants.EDIT_REFERRAL)
          : t(referralConstants.CREATE_REFERRAL)}
      </Heading>
      <Divider />
      <StyledForm
        form={form}
        onFinish={handleSubmit}
        layout="horizontal"
        key={referral?._id || 'new'}
      >
        <FormItem
          name="referralDate"
          label={t(referralConstants.REFERRAL_DATE)}
          rules={[
            {
              required: true,
              message: t(referralConstants.REFERRAL_DATE_REQUIRED)
            }
          ]}
        >
          <AntdDatePicker
            format="DD/MM/YYYY"
            style={{ width: '100%' }}
            placeholder={t(referralConstants.REFERRAL_DATE_PLACEHOLDER)}
          />
        </FormItem>

        <FormItem
          name="referredTo"
          label={t(referralConstants.REFERRED_TO)}
          rules={[
            {
              required: true,
              message: t(referralConstants.REFERRED_TO_REQUIRED)
            },
            {
              max: 500,
              message: t(referralConstants.REFERRED_TO_MAX_LENGTH)
            }
          ]}
        >
          <Input
            maxLength={500}
            showCount
            placeholder={t(referralConstants.REFERRED_TO_PLACEHOLDER)}
          />
        </FormItem>

        <FormItem
          name="reasonForReferral"
          label={t(referralConstants.REASON_FOR_REFERRAL)}
          rules={[
            {
              required: true,
              message: t(referralConstants.REASON_FOR_REFERRAL_REQUIRED)
            },
            {
              max: 5000,
              message: t(referralConstants.REASON_FOR_REFERRAL_MAX_LENGTH)
            }
          ]}
        >
          <TextArea
            rows={5}
            maxLength={5000}
            showCount
            placeholder={t(referralConstants.REASON_FOR_REFERRAL_PLACEHOLDER)}
          />
        </FormItem>

        <FormItem label={<Text>{t(referralConstants.ATTACHMENTS)}</Text>}>
          {isEditMode && existingAttachmentsCount > 0 && (
            <Text style={{ fontSize: '12px', marginBottom: '8px', display: 'block' }}>
              {existingAttachmentsCount} existing attachment{existingAttachmentsCount > 1 ? 's' : ''}.
              You can add {remainingSlots} more.
            </Text>
          )}
          <Upload
            fileList={fileList}
            beforeUpload={() => false}
            onChange={handleFileChange}
            accept=".pdf,.png,.jpg,.jpeg,.heic,.docm,.gif,.docx,.doc,.xlsx,.xls,.txt,.csv,.ppt,.pptx"
            maxCount={isEditMode ? remainingSlots : MAX_ATTACHMENTS}
          >
            <Button disabled={isEditMode ? fileList.length >= remainingSlots : fileList.length >= MAX_ATTACHMENTS}>
              <UploadOutlined />
              {t(referralConstants.ADD_DOCUMENT)}
              {isEditMode
                ? fileList.length > 0 && ` (${fileList.length}/${remainingSlots})`
                : fileList.length > 0 && ` (${fileList.length}/${MAX_ATTACHMENTS})`
              }
            </Button>
          </Upload>
          {((isEditMode && fileList.length >= remainingSlots) || (!isEditMode && fileList.length >= MAX_ATTACHMENTS)) && (
            <Text style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
              {isEditMode
                ? `Maximum ${remainingSlots} additional attachment${remainingSlots !== 1 ? 's' : ''} allowed`
                : t(referralConstants.ATTACHMENTS_MAX)
              }
            </Text>
          )}
        </FormItem>

        <Divider />
      </StyledForm>
    </Modal>
  )
}

const StyledForm = styled(AntdForm)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const ModalFooter = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
`

export default ReferralModal
